import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import * as RadixDialog from '@radix-ui/react-dialog';
import { ReactNode, useState } from 'react';
import { GlassMagnifier } from 'react-image-magnifiers';

import { Close, RotateLeft, RotateRight } from '@lichtblick/icons';
import { Colors } from '@lichtblick/theme';

import { ImageContainer, RotationControls } from './ImageDialog.styles';

import { IconButton } from '../../IconButton';
import { Overlay, CloseButton } from '../Dialog.styles';

type ImageDialogProps = {
  alt: string;
  children?: ReactNode;
  ratio: number;
  src: string;
  trigger?: React.ReactElement;
};

export const ImageDialog = ({ alt, children, ratio, src, trigger }: ImageDialogProps) => {
  const [rotation, setRotation] = useState(0);

  const rotateLeft = () => setRotation((prev) => (prev - 90) % 360);
  const rotateRight = () => setRotation((prev) => (prev + 90) % 360);

  const isRotated = rotation % 180 !== 0;
  const effectiveRatio = isRotated ? 1 / ratio : ratio;

  return (
    <RadixDialog.Root>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      {children && <RadixDialog.Trigger asChild>{children}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <Overlay>
          <ImageContainer $ratio={effectiveRatio}>
            <AspectRatio ratio={ratio}>
              {rotation === 0 ? (
                <GlassMagnifier
                  cursorStyle="zoom-in"
                  imageAlt={alt}
                  imageSrc={src}
                  magnifierBorderColor={Colors.blue[50]}
                  magnifierBorderSize={2}
                  magnifierSize={'40%'}
                  square={true}
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element -- used outside Next.js app context (Storybook)
                <img
                  alt={alt}
                  src={src}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transform: `rotate(${rotation}deg)`,
                  }}
                />
              )}
            </AspectRatio>
            <CloseButton asChild>
              <IconButton icon={<Close />} variant="ghost" />
            </CloseButton>
            <RotationControls>
              <IconButton icon={<RotateLeft />} onClick={rotateLeft} variant="ghost" />
              <IconButton icon={<RotateRight />} onClick={rotateRight} variant="ghost" />
            </RotationControls>
          </ImageContainer>
        </Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};
